import { useState } from "react";
import { Lock, Loader2, Clock, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const SHAPES = [
  { id: "slim", label: "Slim" },
  { id: "classic", label: "Classic" },
  { id: "athletic", label: "Athletic" },
  { id: "fuller", label: "Fuller" },
];

// Item 7 — Body shape. Changing it redoes the FRONT view only (one paid render) and asks for the side views again,
// so it always confirms first, is capped per duck and is locked once a sculpt exists. While the spend gate is closed
// the choice is saved and carried out by itself — the old front view stays on screen and approvals are untouched.
export const BodyShapePicker = ({ subject, busy, onChange }) => {
  const [ask, setAsk] = useState(null);
  if (!subject?.body_shape_applies) return null;

  const pending = subject.pending;
  const queued = pending?.state === "queued";
  const failed = pending?.state === "failed";
  const locked = !!subject.locked;
  const exhausted = !failed && subject.changes_left <= 0;
  const disabled = locked || !!busy || exhausted;
  const current = queued ? pending.shape : subject.body_shape;

  const confirm = async () => {
    const shape = ask;
    setAsk(null);
    if (shape) await onChange(shape);
  };

  return (
    <div data-testid="body-shape-picker">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
        <p className="text-xs font-mono2 uppercase tracking-widest text-muted-foreground">Body shape</p>
        {!locked && !queued && (
          <p data-testid="body-shape-changes-left" className="text-xs text-muted-foreground">
            {subject.changes_left} of {subject.max_changes} changes left
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {SHAPES.map((s) => {
          const active = current === s.id;
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              data-testid={`body-shape-${s.id}`}
              onClick={() => !active && setAsk(s.id)}
              className={`rounded-full py-2 text-sm font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"
              }`}
            >
              {busy === "body_shape" && active ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : s.label}
            </button>
          );
        })}
      </div>

      {locked ? (
        <p data-testid="body-shape-locked" className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {subject.lock_note}
        </p>
      ) : queued ? (
        <p data-testid="body-shape-queued" className="mt-2 flex items-start gap-1.5 text-xs text-accent font-semibold">
          <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {pending.message}
        </p>
      ) : failed ? (
        <div className="mt-2 space-y-2">
          <p data-testid="body-shape-failed" className="flex items-start gap-1.5 text-xs text-destructive font-semibold">
            {pending.message}
          </p>
          <button
            type="button"
            data-testid="body-shape-redo"
            disabled={!!busy}
            onClick={() => onChange(pending.shape)}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-3 py-1.5 text-xs font-bold hover:bg-accent/25 disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Redo
          </button>
        </div>
      ) : exhausted ? (
        <p data-testid="body-shape-exhausted" className="mt-2 text-xs text-muted-foreground">
          You have used all {subject.max_changes} body-shape changes on this duck — make a new duck to try another shape.
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">Changing this redoes the front view only — your 3D model is never re-sculpted.</p>
      )}

      <AlertDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)}>
        <AlertDialogContent data-testid="body-shape-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Change the body shape?</AlertDialogTitle>
            <AlertDialogDescription>
              {subject.confirm_note} Your approved views stay until the new front view is ready.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="body-shape-cancel" className="rounded-full">Keep it as it is</AlertDialogCancel>
            <AlertDialogAction data-testid="body-shape-confirm-btn" className="rounded-full font-bold" onClick={confirm}>
              Yes, redo the front view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
